'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  href: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-home', label: 'Dashboard', icon: 'HomeIcon', href: '/customer-dashboard' },
  { id: 'nav-shop', label: 'Shop', icon: 'ShoppingBagIcon', href: '/shop' },
  { id: 'nav-orders', label: 'My Orders', icon: 'ClipboardDocumentListIcon', href: '/customer-dashboard/orders' },
  { id: 'nav-payments', label: 'Payments', icon: 'CreditCardIcon', href: '/customer-dashboard/payments' },
  { id: 'nav-returns', label: 'Returns', icon: 'ArrowUturnLeftIcon', href: '/customer-dashboard/returns' },
  { id: 'nav-reviews', label: 'My Reviews', icon: 'StarIcon', href: '/customer-dashboard/reviews' },
  { id: 'nav-wishlist', label: 'Wishlist', icon: 'HeartIcon', href: '/customer-dashboard/wishlist' },
  { id: 'nav-addresses', label: 'Addresses', icon: 'MapPinIcon', href: '/customer-dashboard/addresses' },
  { id: 'nav-loyalty', label: 'Loyalty Points', icon: 'TrophyIcon', href: '/customer-dashboard/loyalty' },
  { id: 'nav-notifications', label: 'Notifications', icon: 'BellIcon', href: '/customer-dashboard/notifications' },
  { id: 'nav-support', label: 'Support', icon: 'ChatBubbleLeftRightIcon', href: '/customer-dashboard/support' },
  { id: 'nav-profile', label: 'My Profile', icon: 'UserCircleIcon', href: '/customer-dashboard/profile' },
];

interface Props {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function CustomerDashboardSidebar({ mobileOpen, onMobileClose }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const [customerName, setCustomerName] = useState('Customer');
  const [customerEmail, setCustomerEmail] = useState('');
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);

  useEffect(() => {
    async function loadData() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name, email, loyalty_points')
        .eq('id', user.id)
        .maybeSingle();
      if (profile) {
        setCustomerName(profile.full_name || 'Customer');
        setCustomerEmail(profile.email || user.email || '');
        setLoyaltyPoints(profile.loyalty_points || 0);
      }
    }
    loadData();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.push('/customer-login');
  };

  const initials = customerName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase() || 'CU';

  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border flex flex-col z-30 transition-transform duration-300
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      {/* Logo */}
      <div className="px-5 py-5 border-b border-border">
        <Link href="/" className="flex items-center gap-2.5 group">
          <AppLogo size={36} />
          <div>
            <span className="font-700 text-foreground text-base block">Bozaat</span>
            <span className="text-xs text-muted-foreground">Customer Portal</span>
          </div>
        </Link>
      </div>

      {/* Customer info */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-700 text-primary">{initials}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-600 text-foreground truncate">{customerName}</p>
            <p className="text-xs text-muted-foreground truncate">{customerEmail}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
          <Icon name="StarIcon" size={14} className="text-amber-500 flex-shrink-0" />
          <span className="text-xs font-600 text-amber-700">{loyaltyPoints.toLocaleString('en-IN')} Loyalty Points</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map(item => {
          const isActive = pathname === item.href || (item.href !== '/customer-dashboard' && pathname.startsWith(item.href));
          return (
            <Link
              key={item.id}
              href={item.href}
              onClick={onMobileClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 transition-colors ${
                isActive
                  ? 'bg-primary text-white' :'text-secondary-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon name={item.icon as never} size={18} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-3 py-4 border-t border-border">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 text-red-600 hover:bg-red-50 transition-colors"
        >
          <Icon name="ArrowRightOnRectangleIcon" size={18} />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
